import React from 'react'
import { cn } from '@/lib/utils'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Plus, Menu } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocation } from '@tanstack/react-router'
import { CasesActionDialog } from '@/views/cases/components/cases-action-dialog'
import { useQueryClient } from '@tanstack/react-query'

import { LanguageSwitcher } from '@/components/language-switcher'
import { ThemeSwitch } from '@/components/theme-switch'
import { NotificationPopover } from '@/components/notifications/notification-popover'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { GuideHelpButton } from '@/components/guide-assistant'

interface HeaderProps extends React.HTMLAttributes<HTMLElement> {
  fixed?: boolean
  ref?: React.Ref<HTMLElement>
}

export const Header = ({
  className,
  fixed,
  children,
  ...props
}: HeaderProps) => {
  const [offset, setOffset] = React.useState(0)
  const { t } = useTranslation()
  const location = useLocation()
  const [isAddCaseOpen, setAddCaseOpen] = React.useState(false)
  const queryClient = useQueryClient()

  // Check if current page is dashboard (either '/' or '/dashboard')
  const isDashboard = location.pathname === '/' || location.pathname === '/dashboard'

  React.useEffect(() => {
    const onScroll = () => {
      setOffset(document.body.scrollTop || document.documentElement.scrollTop)
    }

    // Add scroll listener to the body
    document.addEventListener('scroll', onScroll, { passive: true })

    // Clean up the event listener on unmount
    return () => document.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      className={cn(
        'flex h-16 items-center justify-between gap-4 bg-card border-b border-border px-6',
        'transition-all duration-200',
        fixed && 'header-fixed peer/header fixed z-50 w-[inherit]',
        offset > 10 && fixed ? 'shadow-md' : '',
        className
      )}
      {...props}
    >
      {/* Left side: Sidebar trigger + Search */}
      <div className="flex items-center gap-4 flex-1">
        <SidebarTrigger
          variant='ghost'
          className='text-muted-foreground hover:text-foreground md:hidden'
        >
          <Menu className="h-5 w-5" />
        </SidebarTrigger>
      </div>

      {/* Right side: Actions */}
      <div className="flex items-center gap-3">
        <LanguageSwitcher />
        <ThemeSwitch />
        <GuideHelpButton />
        <NotificationPopover />
        <ProfileDropdown />

        {/* Add New File Button - Only shown on dashboard routes */}
        {isDashboard && (
          <Button
            variant="gold"
            className={cn(
              "hidden sm:flex items-center gap-2",
              "justify-center whitespace-nowrap",
              "text-white", // Override default gold text color if needed, but gold variant usually handles it. Let's keep text-white as requested
              "px-4 py-2 rounded-lg text-sm font-semibold shadow-sm",
              "transition-all duration-200 hover:shadow-md hover:scale-105"
            )}
            onClick={() => setAddCaseOpen(true)}
          >
            <Plus className="h-4 w-4" />
            <span>{t('add_new_file') || 'إضافة ملف جديد'}</span>
          </Button>
        )}

        <CasesActionDialog
          open={isAddCaseOpen}
          onOpenChange={setAddCaseOpen}
          onUpdateSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['dashboard-recent-files'] })
            queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
            queryClient.invalidateQueries({ queryKey: ['cases'] })
          }}
        />

        {/* Mobile menu trigger */}
        <SidebarTrigger
          variant='ghost'
          className='md:hidden text-primary'
        >
          <Menu className="h-5 w-5" />
        </SidebarTrigger>

        {children}
      </div>
    </header>
  )
}

Header.displayName = 'Header'
